import { NgIf } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  Output,
  ViewChild
} from "@angular/core";
import { RouterLink, RouterLinkActive } from "@angular/router";
import { TooltipManagerService } from "@app/shared/services";
import { NgbTooltip } from "@ng-bootstrap/ng-bootstrap";
import { PlacementArray } from "@ng-bootstrap/ng-bootstrap/util/positioning";
import { TranslocoModule } from "@ngneat/transloco";
import { PermissionTypeDto } from "clearline-api";

@Component({
  selector: "app-upgradeable-item",
  templateUrl: "./upgradeable-item.component.html",
  styleUrls: ["./upgradeable-item.component.scss"],
  standalone: true,
  imports: [NgIf, NgbTooltip, TranslocoModule, RouterLink, RouterLinkActive],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UpgradeableItemComponent {
  @Input() permission: PermissionTypeDto;
  @Input() classList = "";
  @Input() isLocked = false;
  @Input() canUpgrade = true;
  @HostBinding("class.is-locked") get lockedClass() {
    return this.isLocked;
  }
  @Input() placement: PlacementArray = ["right-top", "right", "right-bottom", "bottom-left", "top-left"];
  /**
   * Opt-in: render transparent bridge strips above/below the tooltip so it stays open while the
   * cursor moves across the arrow gap from the trigger onto the tooltip. Off by default — existing
   * usages (sidebar, etc.) are unchanged. Useful for top/bottom placement where there is a vertical gap.
   */
  @Input() hoverBridge = false;
  @Input() routerLink: string | any[];
  @Input() routerLinkActiveOptions: { exact: boolean } = { exact: true };
  @Output() upgradeClick = new EventEmitter<PermissionTypeDto>();
  @Output() itemClick = new EventEmitter<void>();

  @ViewChild("tooltip") tooltip: NgbTooltip;
  @ViewChild("anchor", { read: ElementRef }) anchor: ElementRef<HTMLAnchorElement>;

  private hoverTimeout: ReturnType<typeof setTimeout> = null;

  constructor(private tooltipManager: TooltipManagerService) {}

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: Event) {
    this.tooltipManager.closeActiveTooltip();
  }

  getTooltipContent(): string {
    return this.tooltipManager.getLockedTooltipContent(this.permission);
  }

  getInfoTooltipContent(): string {
    return this.tooltipManager.getInfoTooltipContent(this.permission);
  }

  hasInfoTooltip(): boolean {
    return this.tooltipManager.hasInfoTooltip(this.permission);
  }

  onLockedItemHover(): void {
    if (this.hasInfoTooltip() || this.isLocked) {
      this.clearHoverTimeout();
      this.openTooltip();
    }
  }

  onLockedItemLeave(event: MouseEvent): void {
    this.clearHoverTimeout();
    const relatedTarget = event.relatedTarget as HTMLElement;
    // Don't close if mouse goes to this item's tooltip
    if (relatedTarget?.closest("ngb-tooltip-window")) {
      return;
    }
    // Close tooltip when leaving the item
    if (this.tooltip?.isOpen()) {
      this.tooltipManager.detachTooltip();
      this.tooltip.close();
    }
  }

  onTooltipClosed(): void {
    this.tooltipManager.clearTooltip(this.tooltip);
  }

  onTooltipMouseLeave(event: MouseEvent): void {
    const relatedTarget = event.relatedTarget as HTMLElement;
    if (!relatedTarget) {
      this.tooltipManager.closeActiveTooltip();
      return;
    }

    // If mouse returns to trigger — keep tooltip open
    if (this.anchor?.nativeElement?.contains(relatedTarget)) {
      return;
    }

    // If mouse goes to tooltip wrapper (arrow/padding) — attach listener there
    const tooltipWindow = relatedTarget.closest("ngb-tooltip-window");
    if (tooltipWindow) {
      const onWindowLeave = (e: MouseEvent) => {
        tooltipWindow.removeEventListener("mouseleave", onWindowLeave);
        this.handleTooltipLeave(e.relatedTarget as HTMLElement);
      };
      tooltipWindow.addEventListener("mouseleave", onWindowLeave);
      return;
    }

    this.handleTooltipLeave(relatedTarget);
  }

  private handleTooltipLeave(relatedTarget: HTMLElement | null): void {
    if (relatedTarget && this.anchor?.nativeElement?.contains(relatedTarget)) {
      return;
    }

    const goingToSidebar = relatedTarget?.closest(".vertical-menu");
    if (goingToSidebar) {
      this.tooltipManager.detachTooltip();
      if (this.tooltip) {
        this.tooltip.close();
      }
    } else {
      this.tooltipManager.closeActiveTooltip();
    }
  }

  onLockedItemClick(event: Event) {
    event.stopPropagation(); // Always prevent bubbling to parent handlers

    if (this.isLocked) {
      event.preventDefault();
    } else {
      this.itemClick.emit();
    }
  }

  onTooltipContentClick(event: Event) {
    event.stopPropagation();
  }

  onUpgradeClick(): void {
    this.closeTooltip();
    this.upgradeClick.emit(this.permission);
  }

  private clearHoverTimeout(): void {
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }
  }

  private closeTooltip(): void {
    if (this.tooltip) {
      this.tooltip.close();
    }
  }

  private openTooltip(): void {
    this.hoverTimeout = setTimeout(() => {
      if (!this.tooltip.isOpen()) {
        this.tooltipManager.registerTooltip(this.tooltip);
        this.tooltip.open();
      }
      this.hoverTimeout = null;
    }, 200);
  }
}
